import React, { useState } from "react";

// manuellt skrevet inn i package.json:
// "deploy": "react-scripts build && cp -r build/* /var/www/fartskalkulator/"
// for å auto få build filene til server og online (npm run deploy)

const raceDistances = [
  { label: "400m", value: 0.4 },
  { label: "800m", value: 0.8 },
  { label: "1000m", value: 1 },
  { label: "1500m", value: 1.5 },
  { label: "1 Mile", value: 1.609 },
  { label: "3000", value: 3 },
  { label: "5K", value: 5 },
  { label: "10K", value: 10 },
  { label: "Halvmaraton", value: 21.1 },
  { label: "Maraton", value: 42.195 },
];

// Format seconds to H:MM:SS or M:SS
function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.round(totalSeconds % 60);

  const pad = (num) => num.toString().padStart(2, "0");

  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  else return `${m}:${pad(s)}`;
}

function App() {
  // Distanse og tid inputs
  const [distance, setDistance] = useState("");
  const [selectedDistance, setSelectedDistance] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");

  // Pace inputs (min og sek per km)
  const [paceMinutes, setPaceMinutes] = useState("");
  const [paceSeconds, setPaceSeconds] = useState("");

  // Resultater
  const [calculatedPace, setCalculatedPace] = useState(null); // string "M:SS per km"
  const [paceInSeconds, setPaceInSeconds] = useState(null); // pace in seconds per km

  const handleSelectChange = (e) => {
    const val = e.target.value;
    setSelectedDistance(val);
    setDistance(val);
  };

  const handleDistanceChange = (e) => {
    const val = e.target.value;
    setDistance(val);
    if (val !== selectedDistance) {
      setSelectedDistance("");
    }
  };

  const calculateFromTime = () => {
    // Calculate pace from time + distance
    const dist = parseFloat(distance);
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    const s = parseInt(seconds) || 0;

    if (!dist || dist <= 0) {
      alert("Vennligst skriv en gyldig distanse");
      return false;
    }

    const totalSeconds = h * 3600 + m * 60 + s;
    if (totalSeconds <= 0) {
      alert("Vennligst skriv en gyldig tid");
      return false;
    }

    const paceSec = totalSeconds / dist;
    setPaceInSeconds(paceSec);

    const paceMins = Math.floor(paceSec / 60);
    const paceSecs = Math.round(paceSec % 60);

    setCalculatedPace(`${paceMins}:${paceSecs.toString().padStart(2, "0")} per km`);
    return true;
  };

  const calculateFromPace = () => {
    // Calculate times based on pace
    const pm = parseInt(paceMinutes);
    const ps = parseInt(paceSeconds);

    if (isNaN(pm) || pm < 0 || isNaN(ps) || ps < 0 || ps >= 60) {
      alert("Vennligste skriv en gyldig fart (minutter og sekunder per km");
      return false;
    }

    const paceSec = pm * 60 + ps;
    if (paceSec === 0) {
      alert("Fart kan ikke være 0");
      return false;
    }

    setPaceInSeconds(paceSec);
    setCalculatedPace(`${pm}:${ps.toString().padStart(2, "0")} per km`);
    return true;
  };

  const calculate = () => {
    if (distance && (hours || minutes || seconds)) {
      if (calculateFromTime()) {
        setPaceMinutes("");
        setPaceSeconds("");
      }
    } else if (paceMinutes !== "" || paceSeconds !== "") {
      if (calculateFromPace()) {
        setDistance("");
        setSelectedDistance("");
        setHours("");
        setMinutes("");
        setSeconds("");
      }
    } else {
      alert("Vennligst skriv enten distanse + tid, eller fart");
    }
  };

  const reset = () => {
    setDistance("");
    setSelectedDistance("");
    setHours("");
    setMinutes("");
    setSeconds("");
    setPaceMinutes("");
    setPaceSeconds("");
    setCalculatedPace(null);
    setPaceInSeconds(null);
  };

  return (
    <div style={styles.container}>
      <h1>Fartskalkulator</h1>

      <div style={styles.inputGroup}>
        <label>
        </label>
      </div>

      <div style={{ ...styles.inputGroup, borderTop: "3px solid #ccc", paddingTop: 15 }}>
        <label>
          Velg en distanse:
          <select value={selectedDistance} onChange={handleSelectChange} style={styles.select}>
            <option value="">-- Velg distanse --</option>
            {raceDistances.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={styles.inputGroup}>
        <label>
          Skriv inn egendefinert distanse (km):
          <input
            type="number"
            min="0"
            step="any"
            value={distance}
            onChange={handleDistanceChange}
            placeholder=""
            style={styles.input}
          />
        </label>
      </div>

      <div style={styles.inputGroup}>
        <label>
          Tid:
          <div style={styles.timeInputs}>
            <input
              type="number"
              min="0"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Timer"
              style={styles.timeInput}
            />
            <input
              type="number"
              min="0"
              max="59"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="Minutter"
              style={styles.timeInput}
            />
            <input
              type="number"
              min="0"
              max="59"
              value={seconds}
              onChange={(e) => setSeconds(e.target.value)}
              placeholder="Sekunder"
              style={styles.timeInput}
            />
          </div>
        </label>
      </div>

      <div style={{ ...styles.inputGroup, borderTop: "3px solid #ccc", paddingTop: 15 }}>
        <label>
          Eller skriv inn fart (minutter og sekunder per km):
          <div style={styles.timeInputs}>
            <input
              type="number"
              min="0"
              value={paceMinutes}
              onChange={(e) => setPaceMinutes(e.target.value)}
              placeholder="Minutter"
              style={styles.timeInput}
            />
            <input
              type="number"
              min="0"
              max="59"
              value={paceSeconds}
              onChange={(e) => setPaceSeconds(e.target.value)}
              placeholder="Sekunder"
              style={styles.timeInput}
            />
          </div>
        </label>
      </div>

      <button onClick={calculate} style={styles.button}>
        Kalkuler
      </button>
      <button onClick={reset} style={{ ...styles.button, marginLeft: 10 }}>
        Reset
      </button>

      {paceInSeconds && (
        <>
          <div style={styles.result}>
            <h2>Din fart er:</h2>
            <p style={{ fontSize: "1.5em" }}>{calculatedPace}</p>
          </div>

          <div style={{ marginTop: 20, textAlign: "left" }}>
            <h3>Estimert tid for vanlige distanser:</h3>
            <ul>
              {raceDistances.map(({ label, value }) => {
                const totalSeconds = paceInSeconds * value;
                return (
                  <li key={label} style={{ marginBottom: 6 }}>
                    <strong>{label}:</strong> {formatTime(totalSeconds)}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

<footer style={styles.footer}>
  <p>
    Martin Styve Pedersen &copy; 2025. Built with React.
  </p>
</footer>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 600,
    margin: "40px auto",
    padding: 20,
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  inputGroup: {
    marginBottom: 20,
    textAlign: "left",
  },
  select: {
    marginLeft: 10,
    padding: "6px 10px",
    fontSize: 16,
  },
  input: {
    marginLeft: 10,
    padding: "6px 8px",
    fontSize: 16,
    width: "100px",
  },
  timeInputs: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 5,
  },
  timeInput: {
    width: "30%",
    padding: "6px 8px",
    fontSize: 16,
  },
  button: {
    backgroundColor: "#007bff",
    border: "none",
    color: "white",
    padding: "10px 15px",
    fontSize: 16,
    cursor: "pointer",
    borderRadius: 4,
  },
  result: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#e9f7ef",
    borderRadius: 6,
  },
  footer: {
  marginTop: 40,
  paddingTop: 15,
  borderTop: "1px solid #ddd",
  fontSize: 14,
  color: "#666",
}
};

export default App;